"use client";

import { useEffect, useImperativeHandle, useRef } from "react";

/**
 * Renders the Cloudflare Turnstile widget.
 *
 * Why this is more involved than `<div class="cf-turnstile" />`:
 *
 * Cloudflare's `api.js` auto-renders any `.cf-turnstile` divs it finds *at
 * script load time*. After that initial sweep it never scans again. In an
 * SPA flow like our checkout, the widget mounts on step 4 — by which point
 * `api.js` was loaded earlier (or got loaded during this mount) and the
 * auto-render has already fired with no widgets to find. Result: empty
 * container, no challenge rendered, "Place order" stays disabled.
 *
 * Fix: load the script with `?render=explicit`, wait for `window.turnstile`
 * to exist (poll briefly because the script doesn't expose a load event we
 * can listen for), then call `turnstile.render(container, { sitekey, callback })`
 * ourselves. Track the widget id so we can `turnstile.remove(id)` on unmount
 * — without that, going back a step then forward again leaves the previous
 * widget orphaned and the new mount renders a duplicate.
 */

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileGlobal {
  render: (
    container: HTMLElement | string,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: (code?: string) => void;
      "timeout-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
      size?: "normal" | "compact" | "invisible";
      /** Auto-retry on a recoverable error (Cloudflare default is "auto"). */
      retry?: "auto" | "never";
      "retry-interval"?: number;
      /** Auto-refresh an expired token so a stale token never blocks submit. */
      "refresh-expired"?: "auto" | "manual" | "never";
      /** Auto-recover if the interactive challenge times out. */
      "refresh-timeout"?: "auto" | "manual" | "never";
    }
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

function getTurnstile(): TurnstileGlobal | undefined {
  return (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
}

/** Inject the Turnstile script once per page. Resolves when window.turnstile is ready. */
function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (getTurnstile()) return Promise.resolve();

  // If a previous mount already started loading, reuse that promise.
  const w = window as unknown as { __turnstileLoad?: Promise<void> };
  if (w.__turnstileLoad) return w.__turnstileLoad;

  w.__turnstileLoad = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="${TURNSTILE_SCRIPT_SRC.split("?")[0]}"]`
    );
    if (existing) {
      // Script tag exists from a prior render; poll until the global lands.
      pollUntilReady(resolve, reject);
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => pollUntilReady(resolve, reject);
    script.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(script);
  });
  return w.__turnstileLoad;
}

/**
 * `script.onload` fires before `window.turnstile` is wired up on some
 * browsers, so poll briefly for the global. 50 × 100ms = up to 5s before
 * giving up.
 */
function pollUntilReady(
  resolve: () => void,
  reject: (err: Error) => void
): void {
  let tries = 0;
  const tick = () => {
    if (getTurnstile()) {
      resolve();
      return;
    }
    if (++tries >= 50) {
      reject(new Error("Turnstile global never appeared after script load"));
      return;
    }
    setTimeout(tick, 100);
  };
  tick();
}

export interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  /** Fired when the challenge errors or times out (network, blocked, failed
   *  interactive challenge). Lets the parent surface a "try again" affordance
   *  instead of leaving the customer stuck with no token. */
  onError?: () => void;
  /** Widget theme: light, dark, or auto */
  theme?: "light" | "dark" | "auto";
  /** Size: normal, compact, or invisible */
  size?: "normal" | "compact" | "invisible";
  /** Optional custom class for the container */
  className?: string;
  /**
   * REACT19-2 — `ref` is a REGULAR PROP in React 19; no `forwardRef` wrapper.
   * It still feeds `useImperativeHandle` below, so the `TurnstileWidgetHandle`
   * contract every caller relies on (`turnstileRef.current?.reset()`) is
   * unchanged. All four call sites pass a `useRef<TurnstileWidgetHandle>(null)`.
   */
  ref?: React.Ref<TurnstileWidgetHandle>;
}

/** Imperative handle so a parent can force a fresh token. */
export interface TurnstileWidgetHandle {
  /**
   * Re-run the challenge and issue a NEW token. Turnstile tokens are
   * SINGLE-USE — once the server verifies a token it's spent, so any retry that
   * re-sends the same token fails with `timeout-or-duplicate`. Callers MUST
   * reset after every submit attempt (success or failure) so the next attempt
   * has a fresh token; otherwise a second submit can never pass verification
   * until the whole page is reloaded.
   */
  reset: () => void;
}

export function TurnstileWidget({
  onVerify,
  onExpire,
  onError,
  theme = "auto",
  size = "normal",
  className = "",
  ref,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        const turnstile = getTurnstile();
        const id = widgetIdRef.current;
        if (turnstile && id) {
          try {
            turnstile.reset(id);
          } catch {
            /* widget not rendered yet / already reset — safe to ignore */
          }
        }
      },
    }),
    []
  );

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  }, [onVerify, onExpire, onError]);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    loadTurnstileScript()
      .then(() => {
        if (cancelled) return;
        const turnstile = getTurnstile();
        if (!turnstile) return;
        widgetIdRef.current = turnstile.render(container, {
          sitekey: siteKey,
          callback: (token: string) => onVerifyRef.current(token),
          "expired-callback": () => onExpireRef.current?.(),
          // A challenge error/timeout no longer dead-ends the checkout: notify
          // the parent (which shows a "try again" prompt) and let Turnstile
          // auto-retry the recoverable cases.
          "error-callback": () => onErrorRef.current?.(),
          "timeout-callback": () => onErrorRef.current?.(),
          retry: "auto",
          "refresh-expired": "auto",
          "refresh-timeout": "auto",
          theme,
          size,
        });
      })
      .catch((err) => {
        // Surface in dev tools but don't crash the checkout — server-side
        // verification is the real gate; the widget is best-effort UX.
        console.error("[TurnstileWidget] failed to render:", err);
      });

    return () => {
      cancelled = true;
      const turnstile = getTurnstile();
      const id = widgetIdRef.current;
      if (turnstile && id) {
        try {
          turnstile.remove(id);
        } catch {
          /* widget may already be torn down */
        }
        widgetIdRef.current = null;
      }
    };
    // siteKey/theme/size are stable per session; re-renders on those
    // would tear down the widget unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} className={className} />;
}
